import { isEmpty } from "lodash/fp";
import { Path } from "react-router-dom";

import { PartialBy } from "@app/common/types";

type ParamValue =
  | (number | string)[]
  | string
  | number
  | boolean
  | null
  | undefined;

type Param = ParamValue | ParamValue[];

type ObjectParams = {
  [key: string]: Param;
};

export type Params = ObjectParams | string;

export const getRedirectTo = (queryParams: URLSearchParams) => {
  const redirectToParam = queryParams.get("redirectTo");
  if (
    redirectToParam &&
    (!redirectToParam.startsWith("http") || !redirectToParam.startsWith("/"))
  ) {
    return decodeURIComponent(redirectToParam);
  }
  return redirectToParam;
};

export const combineQueryParams = (params1: string, params2?: string) => {
  if (!params2) return params1;

  const initialParams = new URLSearchParams(params1);
  const paramsToAdd = new URLSearchParams(params2);

  return new URLSearchParams({
    ...Object.fromEntries(initialParams),
    ...Object.fromEntries(paramsToAdd),
  }).toString();
};

// 0 and false are valid falsy param values, so can't just check for truthiness
export const isNonEmptyValue = (value: Param) => value != null && value !== "";

const addParams = (
  searchParams: URLSearchParams,
  paramsToAdd: ObjectParams
) => {
  Object.entries(paramsToAdd).forEach(([key, value]) => {
    if (Array.isArray(value) && !isEmpty(value)) {
      value.forEach((val) => {
        if (isNonEmptyValue(val)) {
          searchParams.append(key, `${val}`);
        }
      });
    } else if (isNonEmptyValue(value)) {
      searchParams.set(key, decodeURIComponent(`${value}`));
    }
  });
};

export const getObjectFromQueryParams = (
  queryParams: URLSearchParams
): { [key: string]: string } => {
  const params: { [key: string]: string } = {};
  queryParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
};

export const getQueryParamsFromObject = (
  params: ObjectParams,
  initialParams?: URLSearchParams
) => {
  const queryParams = initialParams ?? new URLSearchParams();
  addParams(queryParams, params);
  return queryParams;
};

/**
 * Generates a Path given a base path and params to append or omit
 * */
export const withQueryParams = (
  path: string,
  params?: Params,
  currentParams?: URLSearchParams | string,
  omit?: string[]
): PartialBy<Path, "hash"> => {
  const queryParams: URLSearchParams = new URLSearchParams(currentParams);
  let pathname = path;

  // Handle cases where the path already has query params
  if (pathname.includes("?")) {
    const [pathWithoutSearch, search] = pathname.split("?");
    const searchParams = new URLSearchParams(search);
    searchParams.forEach((value, key) => queryParams.set(key, value));
    pathname = pathWithoutSearch;
  }

  if (typeof params === "string") {
    const searchParams = new URLSearchParams(params);
    searchParams.forEach((value, key) => queryParams.set(key, value));
  } else {
    addParams(queryParams, params ?? {});
  }

  if (omit) {
    for (const key of omit) {
      queryParams.delete(key);
    }
  }

  return { pathname, search: queryParams.toString() };
};

export const withoutQueryParams = (
  path: string,
  currentParams: URLSearchParams | string | undefined,
  omit: string[]
) => withQueryParams(path, undefined, currentParams, omit);

/**
 * Generates a raw URL string given a base path and params to append or omit
 * */
export const withQueryParamsUrl = (
  path: string,
  params?: Params,
  currentParams?: URLSearchParams | string,
  omit?: string[]
): string => {
  const toPath = withQueryParams(path, params, currentParams, omit);
  return (
    (toPath.search ? `${toPath.pathname}?${toPath.search}` : toPath.pathname) ??
    path
  );
};

export const appendQueryParamsToUrl = (url: string, params?: ObjectParams) => {
  const parsedUrl = new URL(url);
  const searchParams = new URLSearchParams(parsedUrl.search);
  addParams(searchParams, params ?? {});
  parsedUrl.search = searchParams.toString();
  return parsedUrl.toString();
};

export const parseUrl = (path: string) => {
  const parsedPath = decodeURIComponent(path);
  const [rootPath] = parsedPath.split("?");
  return rootPath.replace(window.origin, "");
};

/**
 * Parses an encoded URL and appends additional params on top of any that are already present.
 * Will detect if URL is already unencoded and override existing path params with supplied params
 * Generates a Path
 * */
export const parseUrlWithParams = (path: string, params?: Params) => {
  const parsedPath = path.includes("?") ? path : decodeURIComponent(path);
  const [, queryParams] = parsedPath.split("?");

  return withQueryParams(parseUrl(path), params, queryParams);
};

/**
 * Parses an encoded URL and appends additional params on top of any that are already present.
 * Generates a raw URL string.
 * */
export const parseUrlWithParamsUrl = (path: string, params?: Params) => {
  const parsedToPath = parseUrlWithParams(path, params);
  return parsedToPath.search
    ? `${parsedToPath.pathname}?${parsedToPath.search}`
    : parsedToPath.pathname;
};
